import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { buildErrorMessage } from 'utils'
import {
  InformalFeedbackRequestsClient,
  InformalFeedbackReview,
  InformalFeedbackReviewScore,
} from 'api/clients/InformalFeedbackRequestsClient'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { SUBMIT_INFORMAL_FEEDBACK_REVIEW_EVENT, trackEvent } from 'utils/trackingUtils'
import { useCurrentWorkspace } from '../../../useCurrentWorkspace'
import { useAuthOrUnregisteredUser } from '../../../useAuthOrUnregisteredUser'
import { workspaceTasksKeys } from 'screens/Todos/hooks/useWorkspaceTasks'
import { reportError } from 'utils/error'
import { useRefreshInformalFeedbackRequests } from 'hooks/useRefreshInformalFeedbackRequests'

interface Props {
  id: number
  requestId?: number
  almanacFileId?: string
}

export interface Data {
  informal_feedback_review: InformalFeedbackReview
}

export interface Attributes {
  message: string
  score: InformalFeedbackReviewScore
}

export type Options = Omit<UseMutationOptions<Data, unknown, Attributes>, 'mutationKey' | 'mutationFn'>

export const useSubmitReview = ({ id, requestId, almanacFileId }: Props, options?: Options) => {
  const queryClient = useQueryClient()
  const auth = useAuthOrUnregisteredUser().user
  const workspace = useCurrentWorkspace()
  const { profile } = useMemberProfile(workspace?.id, auth?.id)
  const { refreshRequests } = useRefreshInformalFeedbackRequests({
    almanacFileId,
    requestId,
  })

  return useMutation<Data, unknown, Attributes>(
    'submitReview',
    async ({ message, score }) => {
      trackEvent(SUBMIT_INFORMAL_FEEDBACK_REVIEW_EVENT, profile)

      const response = await InformalFeedbackRequestsClient.submitReview(id, {
        message,
        score,
      })

      if (response.type !== SUCCESS_RESPONSE_TYPE) {
        reportError(`Failed to submit review`, {
          metaData: {
            props: { id, message, score },
            response: response.data,
          },
        })
        throw new Error(
          buildErrorMessage(response.data.errors) ||
            'An error occurred! Our team has been notified and we are looking into it.',
        )
      }
      return response.data
    },
    {
      ...options,
      onSuccess: async (data, variables, context) => {
        refreshRequests()
        if (workspace?.key) queryClient.invalidateQueries(workspaceTasksKeys.navMenuCounter(workspace.key))
        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
